import {
  createPublicClient,
  decodeEventLog,
  hexToString,
  http,
  parseAbi,
  toHex,
  type Address,
  type WalletClient,
} from "viem";
import { baseSepolia } from "viem/chains";
import type { Rarity, TraitSelection } from "@/types";

export const ANISOUL_ABI = parseAbi([
  "function mintCost() view returns (uint256)",
  "function mint((uint8 base,uint8 eyes,uint8 hair,uint8 hairColor,uint8 outfit,uint8 accessory,uint8 effect) traits, bytes personalityData) payable returns (uint256 tokenId, uint64 sequenceNumber)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function personalityOf(uint256 tokenId) view returns (bytes)",
  "function updatePersonality(uint256 tokenId, bytes personalityData)",
  "function isTraitHashUsed(bytes32 traitHash) view returns (bool)",
  "function tokenData(uint256 tokenId) view returns (uint8 base,uint8 eyes,uint8 hair,uint8 hairColor,uint8 outfit,uint8 accessory,uint8 effect,uint8 background,uint8 rarity,bool revealed)",
  "event MintRequested(uint256 indexed tokenId, address indexed owner, bytes32 indexed traitHash, uint64 sequenceNumber)",
  "event Revealed(uint256 indexed tokenId, uint8 rarity, uint8 background)",
  "event PersonalityUpdated(uint256 indexed tokenId, address indexed owner, bytes personalityData)",
]);

export const CONTRACT_ADDRESS = import.meta.env.VITE_ANISOUL_CONTRACT as Address | undefined;
export const RPC_URL = import.meta.env.VITE_BASE_SEPOLIA_RPC || "https://sepolia.base.org";

export const publicClient = createPublicClient({
  chain: baseSepolia,
  transport: http(RPC_URL),
});

export const rarityNames: Rarity[] = ["Unrevealed", "Common", "Rare", "Epic", "Legendary"];

export function requireContractAddress(): Address {
  if (!CONTRACT_ADDRESS) throw new Error("VITE_ANISOUL_CONTRACT is not configured.");
  return CONTRACT_ADDRESS;
}

export async function getMintCost() {
  if (!CONTRACT_ADDRESS) return 1_015_000_000_000_000n;
  return publicClient.readContract({ address: CONTRACT_ADDRESS, abi: ANISOUL_ABI, functionName: "mintCost" });
}

export async function mintAvatar(
  walletClient: WalletClient,
  account: Address,
  traits: TraitSelection,
  personalityJson: string,
) {
  const address = requireContractAddress();
  const value = await getMintCost();
  const hash = await walletClient.writeContract({
    account,
    chain: baseSepolia,
    address,
    abi: ANISOUL_ABI,
    functionName: "mint",
    args: [traits, toHex(personalityJson)],
    value,
  });
  const receipt = await publicClient.waitForTransactionReceipt({ hash });
  for (const log of receipt.logs) {
    try {
      const decoded = decodeEventLog({ abi: ANISOUL_ABI, data: log.data, topics: log.topics });
      if (decoded.eventName === "MintRequested") {
        return { hash, tokenId: decoded.args.tokenId, sequenceNumber: decoded.args.sequenceNumber };
      }
    } catch {
      // Ignore unrelated logs in the transaction receipt.
    }
  }
  throw new Error("Mint completed but the token event was not found.");
}

export async function updateOnchainPersonality(
  walletClient: WalletClient,
  account: Address,
  tokenId: bigint,
  personalityJson: string,
) {
  const hash = await walletClient.writeContract({
    account,
    chain: baseSepolia,
    address: requireContractAddress(),
    abi: ANISOUL_ABI,
    functionName: "updatePersonality",
    args: [tokenId, toHex(personalityJson)],
  });
  await publicClient.waitForTransactionReceipt({ hash });
  return hash;
}

const decodeBase64 = (value: string) => {
  const decoded = atob(value);
  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export type OnchainAvatar = {
  tokenId: bigint;
  owner: Address;
  traits: TraitSelection;
  background: number;
  rarity: Rarity;
  revealed: boolean;
  personalityJson: string;
  metadata: { name: string; description: string; image: string; attributes: Array<{ trait_type: string; value: string }> };
};

export async function readOnchainAvatar(tokenId: bigint): Promise<OnchainAvatar> {
  const address = requireContractAddress();
  const [owner, personalityBytes, data, tokenUri] = await Promise.all([
    publicClient.readContract({ address, abi: ANISOUL_ABI, functionName: "ownerOf", args: [tokenId] }),
    publicClient.readContract({ address, abi: ANISOUL_ABI, functionName: "personalityOf", args: [tokenId] }),
    publicClient.readContract({ address, abi: ANISOUL_ABI, functionName: "tokenData", args: [tokenId] }),
    publicClient.readContract({ address, abi: ANISOUL_ABI, functionName: "tokenURI", args: [tokenId] }),
  ]);
  const [, encodedMetadata] = tokenUri.split(",");
  if (!encodedMetadata) throw new Error("NFT metadata is invalid.");
  const metadata = JSON.parse(decodeBase64(encodedMetadata)) as OnchainAvatar["metadata"];
  const [base, eyes, hair, hairColor, outfit, accessory, effect, background, rarity, revealed] = data;
  return {
    tokenId,
    owner,
    traits: { base, eyes, hair, hairColor, outfit, accessory, effect },
    background,
    rarity: rarityNames[rarity] ?? "Unrevealed",
    revealed,
    personalityJson: hexToString(personalityBytes),
    metadata,
  };
}

export const baseScanTokenUrl = (tokenId: bigint) =>
  CONTRACT_ADDRESS ? `https://sepolia.basescan.org/token/${CONTRACT_ADDRESS}?a=${tokenId}` : "https://sepolia.basescan.org";

export const marketplaceTokenUrl = (tokenId: bigint) => {
  const base = import.meta.env.VITE_MARKETPLACE_BASE_URL || "https://testnets.opensea.io/assets/base_sepolia";
  return CONTRACT_ADDRESS ? `${base.replace(/\/$/, "")}/${CONTRACT_ADDRESS}/${tokenId}` : base;
};
